import { useState } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Activity, AlertTriangle, CheckCircle2, Clock, Cloud, Loader2, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';

import type { HistoricalOrdersProgress } from 'src/api/queries/getLastUpdatedAt';
import type { InstallHealth, InstallPhase } from 'src/api/queries/getInstallHealth';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import { useInstallHealth } from 'src/hooks/useInstallHealth';
import { isActiveInstallPhase, withinHours } from 'src/utils/install-phase';
import { paths } from 'src/routes/paths';
import { cn } from 'src/lib/utils';

// ----------------------------------------------------------------------

/** A merchant-facing install whose pipeline has been silent for longer
 *  than this per-phase threshold is treated as stalled.
 *
 *  Source of truth lives backend-side in
 *  `inventory-backend/src/services/install_phase/maintenance_tick.py`
 *  as `PHASE_STUCK_THRESHOLDS` — the per-phase window operators consider
 *  "still healthy" before they're alarmed. The frontend must never show
 *  a "stalled" tone tighter than that, or merchants get a red banner for
 *  installs ops considers progressing normally.
 *
 *  A previous one-size-fits-all 45-minute threshold red-bannered LOADING_FULL
 *  installs that ops happily ticks for up to 24 hours. */
const STALLED_HEARTBEAT_MINUTES: Partial<Record<InstallPhase, number>> = {
  CONNECTING: 60,
  CONNECTED: 30,
  LOADING_RECENT: 4 * 60,
  RECENT_READY: 30,
  LOADING_FULL: 24 * 60,
};

const bannerVariants = cva('relative flex items-center gap-4 overflow-hidden border-b px-5 py-4', {
  variants: {
    tone: {
      neutral: 'border-border bg-muted/45',
      success: 'border-success/25 bg-success/5',
      warning: 'border-warning/30 bg-warning/[0.07]',
      stalled: 'border-destructive/30 bg-destructive/5',
    },
  },
  defaultVariants: {
    tone: 'neutral',
  },
});

const accentVariants = cva('pointer-events-none absolute inset-y-0 left-0 w-0.5 bg-gradient-to-b', {
  variants: {
    tone: {
      neutral: 'from-muted-foreground/25 via-muted-foreground/50 to-muted-foreground/25',
      success: 'from-success/40 via-success/75 to-success/40',
      warning: 'from-warning/50 via-warning/85 to-warning/50',
      stalled: 'from-destructive/50 via-destructive/85 to-destructive/50',
    },
  },
  defaultVariants: {
    tone: 'neutral',
  },
});

const tileVariants = cva(
  'relative flex h-10 w-10 shrink-0 items-center justify-center rounded-[calc(var(--radius)-2px)]',
  {
    variants: {
      tone: {
        neutral: 'bg-muted text-muted-foreground',
        success: 'bg-success/10 text-success',
        warning: 'bg-warning/15 text-warning',
        stalled: 'bg-destructive/10 text-destructive',
      },
    },
    defaultVariants: {
      tone: 'neutral',
    },
  }
);

type BannerTone = NonNullable<VariantProps<typeof bannerVariants>['tone']>;

// ----------------------------------------------------------------------

function formatHours(h: number): string {
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h === 1) return '1 hr';
  return `${h} hrs`;
}

/** Locale-formatted integer ("8,320"). */
function formatCount(n: number): string {
  return n.toLocaleString('en-US');
}

/** Estimate remaining time from elapsed time and orders ingested. Falls back
 *  to the bucket-style "Usually 2–4 hours" when we don't have a rate yet. */
function estimateRemaining(
  ordersIngested: number,
  ordersTotal: number,
  startedAt: string | null
): string | null {
  const remaining = ordersTotal - ordersIngested;
  if (remaining <= 0) return null;

  if (ordersIngested === 0 || !startedAt) {
    return 'Usually 2–4 hours';
  }

  const elapsedMs = Date.now() - new Date(startedAt).getTime();
  const avgMsPerOrder = elapsedMs / ordersIngested;
  const remainingMinutes = (avgMsPerOrder * remaining) / 60_000;

  if (remainingMinutes < 30) return 'Less than 30 min left';

  const remainingHours = remainingMinutes / 60;
  const low = Math.max(0.5, Math.floor(remainingHours * 2) / 2);
  const high = Math.ceil(remainingHours * 2) / 2;

  if (low >= high) return `About ${formatHours(low)} left`;
  return `About ${formatHours(low)}–${formatHours(high)} left`;
}

/** True when an active install has gone silent past the staleness threshold
 *  for its current phase. Phases not listed in `STALLED_HEARTBEAT_MINUTES`
 *  are never considered stalled (the merchant-facing banner does not surface
 *  a stalled tone for terminal or healthy-by-definition phases).
 *
 *  Silence is measured from the later of the last heartbeat and the phase
 *  entry. Only order chunks beat, so CONNECTING and CONNECTED have no
 *  heartbeat at all; the phase entry is also the clock the backend stuck
 *  alarm uses, so the banner never says "we've been notified" before it. */
export function isInstallStalled(installHealth: InstallHealth): boolean {
  const phase = installHealth.installPhase;
  if (!isActiveInstallPhase(phase)) return false;
  const thresholdMinutes = STALLED_HEARTBEAT_MINUTES[phase];
  if (thresholdMinutes == null) return false;
  const lastActivityMs = Math.max(
    ...[installHealth.heartbeatAt, installHealth.phaseEnteredAt]
      .map((at) => (at ? new Date(at).getTime() : Number.NaN))
      .filter((ms) => !Number.isNaN(ms))
  );
  if (!Number.isFinite(lastActivityMs)) return false;
  return Date.now() - lastActivityMs > thresholdMinutes * 60_000;
}

const DISMISSED_STORAGE_KEY = 'install-banner-dismissed';

// ----------------------------------------------------------------------

/** Animated cloud icon with an orbiting loader, for loading states. */
function SyncIcon() {
  return (
    <>
      <Cloud className="h-5 w-5" />
      <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full border border-border bg-card shadow-sm">
        <Loader2 className="h-2.5 w-2.5 animate-spin text-foreground" />
      </span>
    </>
  );
}

// ----------------------------------------------------------------------

const ACTIVELY_LOADING_ORDERS: readonly InstallPhase[] = [
  'CONNECTED',
  'LOADING_RECENT',
  'RECENT_READY',
  'LOADING_FULL',
];

interface BannerCopy {
  tone: BannerTone;
  icon: LucideIcon | typeof SyncIcon;
  title: string;
  description: string | null;
  showProgress: boolean;
  showLink: boolean;
  linkLabel: string;
}

function resolveCopy(
  installHealth: InstallHealth,
  progress: HistoricalOrdersProgress | null
): BannerCopy {
  const phase = installHealth.installPhase;

  // Stalled overlay replaces the phase copy for any silent active install.
  if (isInstallStalled(installHealth)) {
    return {
      tone: 'stalled',
      icon: AlertTriangle,
      title: 'Setup has stalled',
      description: "We've been notified and are looking into it",
      showProgress: false,
      showLink: true,
      linkLabel: 'View details',
    };
  }

  if (phase === 'FULL_READY') {
    return {
      tone: 'success',
      icon: CheckCircle2,
      title: 'All your data is loaded',
      // The demand gate, not the data load, decides when order
      // recommendations appear; "fully set up" is only true once it is open.
      description: installHealth.canShowDemandRecommendations
        ? 'Athory is fully set up across your catalog'
        : 'Order recommendations appear once your forecasts are ready',
      showProgress: false,
      showLink: false,
      linkLabel: 'View details',
    };
  }

  if (phase === 'DEGRADED') {
    return {
      tone: 'warning',
      icon: Clock,
      title: 'Loaded your recent order history',
      description: "We couldn't load your older history — Athory is running on your last 90 days",
      showProgress: false,
      showLink: true,
      linkLabel: 'Why?',
    };
  }

  if (phase === 'BROWSE_ONLY') {
    const credentialsLost = installHealth.lastErrorClass === 'credentials';
    return {
      tone: 'warning',
      icon: Activity,
      title: credentialsLost
        ? 'Reconnect to load your order history'
        : 'Building from your live orders',
      description: credentialsLost
        ? 'Your Shopify connection lost authorization — reconnect to import your past orders'
        : "We couldn't load your past orders — we're learning from new orders as they arrive",
      showProgress: false,
      showLink: true,
      linkLabel: credentialsLost ? 'Reconnect' : 'Why?',
    };
  }

  if (phase != null && ACTIVELY_LOADING_ORDERS.includes(phase)) {
    const ordersIngested = installHealth.ordersIngested ?? 0;
    const ordersTotal = installHealth.ordersTotal ?? 0;
    const hasOrders = ordersTotal > 0;
    const justStarted = phase === 'CONNECTED' && (!hasOrders || ordersIngested === 0);

    let description: string;
    if (justStarted) {
      description = "You can browse your inventory now — we'll keep loading in the background";
    } else if (hasOrders) {
      description = `Loaded ${formatCount(ordersIngested)} of ${formatCount(ordersTotal)} orders`;
    } else {
      description = "We'll keep loading in the background";
    }

    return {
      tone: 'neutral',
      icon: SyncIcon,
      title: 'Loading your order history',
      description,
      showProgress: !!progress,
      showLink: true,
      linkLabel: 'View details',
    };
  }

  // CONNECTING — catalog import, before any orders.
  return {
    tone: 'neutral',
    icon: SyncIcon,
    title: 'Setting up Athory',
    description: 'Importing your product catalog…',
    showProgress: false,
    showLink: true,
    linkLabel: 'View details',
  };
}

// ----------------------------------------------------------------------

/**
 * First-install banner — a `border-b` strip above the page content shown
 * only while a Shopify merchant's first install is loading data. It is a
 * data-loading progress helper: it shows the catalog/order import arriving
 * and never references recommendation readiness.
 *
 * Renders nothing when there is no install in flight (existing companies),
 * when the merchant has dismissed the completed banner, or once the
 * `FULL_READY` banner has aged past its 24h window.
 */
export function InstallBanner({ className }: { className?: string }) {
  const { installHealth, historicalOrdersProgress } = useInstallHealth();
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(DISMISSED_STORAGE_KEY) === '1';
  });

  const handleDismiss = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(DISMISSED_STORAGE_KEY, '1');
    }
    setDismissed(true);
  };

  if (!installHealth || installHealth.installPhase == null) return null;

  const phase = installHealth.installPhase;

  // The completed banner is dismissable and self-expires after 24h.
  if (phase === 'FULL_READY') {
    if (dismissed) return null;
    if (!withinHours(installHealth.phaseEnteredAt, 24)) return null;
  }

  const copy = resolveCopy(installHealth, historicalOrdersProgress);
  const Icon = copy.icon;
  const isLoading = copy.tone === 'neutral';

  const ordersIngested = installHealth.ordersIngested ?? 0;
  const ordersTotal = installHealth.ordersTotal ?? 0;
  const hasOrderProgress = ordersTotal > 0;
  const ordersPct = hasOrderProgress
    ? Math.min(100, Math.round((ordersIngested / ordersTotal) * 100))
    : 0;

  const timeEstimate =
    copy.showProgress && hasOrderProgress
      ? estimateRemaining(ordersIngested, ordersTotal, historicalOrdersProgress?.startedAt ?? null)
      : null;

  // Batches refer to Shopify bulk-op dispatches. Most installs have 1 batch;
  // hide the label entirely in that case — the banner title already signals
  // activity. Internal materialization sub-chunks are never surfaced here.
  const shopifyBatchesTotal = installHealth.shopifyBatchesTotal ?? 0;
  const shopifyBatchesCompleted = Math.min(
    installHealth.shopifyBatchesCompleted ?? 0,
    shopifyBatchesTotal
  );
  const batchLabel =
    copy.showProgress && shopifyBatchesTotal > 1
      ? `Batch ${Math.min(shopifyBatchesCompleted + 1, shopifyBatchesTotal)} of ${shopifyBatchesTotal}`
      : null;

  return (
    <div className={cn(bannerVariants({ tone: copy.tone }), className)}>
      <div className={accentVariants({ tone: copy.tone })} />

      <span className={tileVariants({ tone: copy.tone })}>
        <Icon className="h-5 w-5" />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold text-foreground">{copy.title}</h4>
          {batchLabel && (
            <Badge variant="secondarySoft" className="text-xs">
              {batchLabel}
            </Badge>
          )}
        </div>

        {copy.description && (
          <p className="text-[0.8125rem] text-muted-foreground">{copy.description}</p>
        )}

        {copy.showProgress && hasOrderProgress && (
          <div className="mt-1 flex items-center gap-4">
            <div
              className="h-1 max-w-xs flex-1 overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-valuenow={ordersPct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="h-full rounded-full bg-foreground transition-[width]"
                style={{ width: `${ordersPct}%` }}
              />
            </div>
            {timeEstimate && (
              <span className="shrink-0 text-[0.7rem] text-muted-foreground">{timeEstimate}</span>
            )}
          </div>
        )}
      </div>

      {copy.showLink && (
        <Button asChild variant="ghost" size="sm" className="shrink-0">
          <Link to={paths.setup.root}>{copy.linkLabel} ›</Link>
        </Button>
      )}

      {phase === 'FULL_READY' && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          aria-label="Dismiss"
          onClick={handleDismiss}
        >
          <X className="h-4 w-4" />
        </Button>
      )}

      {isLoading && (
        <div className="pointer-events-none absolute inset-0 -translate-x-full animate-[shimmer_3s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-foreground/[0.035] to-transparent" />
      )}
    </div>
  );
}
